/* Model and effort picker (rules 13, 26; spec 6.11): grouped by what a model does best, not by vendor;
   effort as a 4-step control with a cost hint. Defaults come from the mockup; features pass the
   registry's list from the API when it exists. */
import { Check } from 'lucide-react';
import { Segmented, Toggle } from './basics';

export interface ModelOption { id: string; label?: string; description: string; speed?: 1 | 2 | 3; cost?: 0 | 1 | 2 | 3 | 4; unavailable?: string }
export interface ModelGroup { label: string; models: ModelOption[] }

export const DEFAULT_MODELS: ModelGroup[] = [
  { label: 'Recommended', models: [{ id: 'Auto', description: 'Routes each message; shows the model it used', speed: 3, cost: 1 }] },
  { label: 'Fast', models: [{ id: 'Fast', description: 'Quick answers and drafts', speed: 3, cost: 1 }] },
  { label: 'Smart', models: [{ id: 'Balanced', description: 'Everyday writing and analysis', speed: 2, cost: 2 }, { id: 'Frontier', description: 'Hard questions, long files', speed: 2, cost: 3 }] },
  { label: 'Reasoning', models: [{ id: 'Heavy', description: 'Parallel reasoning: 4 runs, then consensus', speed: 1, cost: 4 }] },
  { label: 'Vision', models: [{ id: 'Vision', description: 'Photos, scans and screenshots', speed: 2, cost: 2 }] },
  { label: 'Specialized', models: [{ id: 'On-device', description: 'Private, offline', speed: 3, cost: 0 }] },
];

export const EFFORTS = [
  { value: 'Fast', hint: 'about 1 credit' },
  { value: 'Standard', hint: '1 to 2 credits' },
  { value: 'Thinking', hint: '2 to 4 credits' },
  { value: 'Heavy', hint: '6 to 10 credits' },
] as const;
export type Effort = (typeof EFFORTS)[number]['value'];

export function EffortDial({ value, onChange }: { value: Effort; onChange: (e: Effort) => void }) {
  return (
    <Segmented<Effort>
      label="Effort"
      value={value}
      onChange={onChange}
      options={EFFORTS.map((e) => ({ value: e.value, label: e.value, title: e.hint }))}
    />
  );
}

export interface ModelPickerProps {
  value: string;
  onChange: (id: string) => void;
  effort: Effort;
  onEffortChange: (e: Effort) => void;
  groups?: ModelGroup[];
  compare?: boolean;
  onCompareChange?: (v: boolean) => void;
}

export function ModelPicker({ value, onChange, effort, onEffortChange, groups = DEFAULT_MODELS, compare, onCompareChange }: ModelPickerProps) {
  const hint = EFFORTS.find((e) => e.value === effort)?.hint;
  return (
    <div className="mp" data-cap="MODEL-02" role="radiogroup" aria-label="Model">
      {groups.map((g) => (
        <div key={g.label} role="group" aria-label={g.label}>
          <div className="mp-grp">{g.label}</div>
          {g.models.map((m) => (
            <button
              key={m.id}
              type="button"
              role="radio"
              aria-checked={value === m.id}
              className="mp-row"
              disabled={!!m.unavailable}
              onClick={() => onChange(m.id)}
            >
              <span className="mp-n">{m.label ?? m.id}{value === m.id && <Check size={14} aria-hidden="true" />}</span>
              <span className="mp-d">{m.unavailable ?? m.description}</span>
              {!m.unavailable && (
                <span className="mp-m" aria-label={`speed ${m.speed ?? 2} of 3, cost ${m.cost ?? 1} of 4`}>
                  {'▮'.repeat(m.speed ?? 2)} {'$'.repeat(m.cost ?? 1) || 'free'}
                </span>
              )}
            </button>
          ))}
        </div>
      ))}
      <div className="mp-grp" data-cap="MODEL-04">Effort</div>
      <div style={{ padding: '2px 8px' }}><EffortDial value={effort} onChange={onEffortChange} /></div>
      <div className="small muted" style={{ padding: '4px 8px' }}>{effort} effort: {hint} per reply</div>
      {onCompareChange && (
        <div style={{ padding: '4px 8px' }} data-cap="MODEL-05">
          <Toggle label="Compare: answer with 2 models side by side" checked={!!compare} onChange={onCompareChange} />
        </div>
      )}
    </div>
  );
}
